// src/components/ProfilePage.js
import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";
import "./ProfilePage.css";

const ProfilePage = ({ onBack, onLogout }) => {
  const [user, setUser] = useState(null);
  const [passwordData, setPasswordData] = useState({ oldPassword: "", newPassword: "" });
  const [message, setMessage] = useState("");
  
  // Plan details state
  const [planDetails, setPlanDetails] = useState({
    planName: "Silver",
    purchaseDate: new Date().toISOString().split('T')[0],
    validityMonths: 3
  });

  // Support queries state
  const [supportQueries, setSupportQueries] = useState([
    { id: '#101954', type: 'Support call', status: 'In progress', date: new Date().toLocaleDateString() }
  ]);

  const supportContacts = {
    chat: { label: 'Chat with us', description: 'Get an instant reply', icon: '💬' },
    call: { label: 'Call us', value: '+91 9108000604', icon: '📞' },
    email: { label: 'Email us', value: 'support@stocks4u.com', icon: '✉️' }
  };

  // Calculate validity end date
  const calculateValidityEnd = () => {
    const start = new Date(planDetails.purchaseDate);
    const end = new Date(start);
    end.setMonth(end.getMonth() + planDetails.validityMonths);
    return end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Format purchase date
  const formatPurchaseDate = () => {
    const date = new Date(planDetails.purchaseDate);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Handle plan selection
  const handlePlanChange = (plan) => {
    const planConfig = {
      Diamond: { validityMonths: 12 },
      Gold: { validityMonths: 6 },
      Silver: { validityMonths: 3 }
    };
    setPlanDetails(prev => ({
      ...prev,
      planName: plan,
      validityMonths: planConfig[plan].validityMonths
    }));
  };

  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        setUser(parsed);
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage("");

    try {
      const response = await fetch(`${API_BASE_URL}/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: user?.email,
          oldPassword: passwordData.oldPassword,
          newPassword: passwordData.newPassword,
        }),
      });

      const data = await response.json();
      if (response.ok && data.status === "success") {
        setMessage("✅ Password updated successfully");
        setPasswordData({ oldPassword: "", newPassword: "" });
      } else {
        setMessage(data.message || "❌ Failed to update password");
      }
    } catch (err) {
      setMessage("❌ Server error, try again later.");
    }
  };

  const userName = user?.email?.split("@")[0] || "Trader";
  const userInitials = userName.substring(0, 2).toUpperCase();

  // Progress logic for timeline indicator
  const start = new Date(planDetails.purchaseDate).getTime();
  const end = new Date(start);
  end.setMonth(end.getMonth() + planDetails.validityMonths);
  const now = new Date().getTime();
  const progressPercent = Math.max(0, Math.min(100, ((now - start) / (end.getTime() - start)) * 100)) || 0;

  return (
    <div className="profile-page-modern">
      <header className="modern-site-header">
        <div className="header-container">
          <img src="images/Final Font Stocks4U1.png" alt="Stocks4U" className="logo-img" />
        </div>
      </header>
      
      <div className="profile-container">
        <button className="back-button" onClick={onBack}>
          <span className="icon">←</span> Back to Dashboard
        </button>

        <div className="user-hero-section">
          <div className="user-avatar-wrap">
            <div className="user-avatar">{userInitials}</div>
          </div>
          <div className="user-greeting-wrap">
            <h1 className="user-greeting">Welcome back, {userName}</h1>
            <p className="user-subtext">Manage your account and subscription preferences.</p>
          </div>
          <button className="logout-button-modern" onClick={onLogout}>
            <span className="icon">⎋</span> Logout
          </button>
        </div>

        <div className="dashboard-grid">
          {/* LEFT COLUMN: Profile & Settings */}
          <div className="grid-left">
            <div className="glass-card personal-card">
              <div className="card-header">
                <h2>Personal Details</h2>
              </div>
              <div className="card-content personal-grid">
                <div className="info-row">
                  <span className="info-icon">👤</span>
                  <div className="info-text">
                    <label>Full Name</label>
                    <span className="value">{userName}</span>
                  </div>
                </div>
                <div className="info-row">
                  <span className="info-icon">✉️</span>
                  <div className="info-text">
                    <label>Email Address</label>
                    <span className="value">{user?.email || "N/A"}</span>
                  </div>
                </div>
                <div className="info-row">
                  <span className="info-icon">📱</span>
                  <div className="info-text">
                    <label>Phone Number</label>
                    <span className="value">+91-XXXXXXXXXX</span>
                  </div>
                </div>
                <div className="info-row">
                  <span className="info-icon">📍</span>
                  <div className="info-text">
                    <label>Location</label>
                    <span className="value">India</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card buy-card">
              <div className="card-header">
                <h2>Subscription Details</h2>
              </div>
              <div className="card-content">
                <div className="timeline-container">
                  <div className="timeline-flex">
                    <div className="timeline-point start">
                      <label>Purchase Date</label>
                      <span className="date">{formatPurchaseDate()}</span>
                    </div>
                    <div className="timeline-point end">
                      <label>Valid Till</label>
                      <span className="date">{calculateValidityEnd()}</span>
                    </div>
                  </div>
                  <div className="timeline-track">
                    <div className="timeline-progress" style={{ width: `${progressPercent}%` }}></div>
                  </div>
                </div>

                <div className="plan-selector">
                  <label>Change Plan Duration:</label>
                  <div className="select-wrapper">
                    <select 
                      value={planDetails.planName} 
                      onChange={(e) => handlePlanChange(e.target.value)}
                      className="modern-select"
                    >
                      <option value="Silver">Silver (3 Months)</option>
                      <option value="Gold">Gold (6 Months)</option>
                      <option value="Diamond">Diamond (12 Months)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card security-card">
              <div className="card-header">
                <h2>Security Preferences</h2>
              </div>
              <div className="card-content">
                <form onSubmit={handlePasswordChange} className="modern-form">
                  <div className="input-group">
                    <label>Current Password</label>
                    <input
                      type="password"
                      name="oldPassword"
                      placeholder="Enter current password"
                      value={passwordData.oldPassword}
                      onChange={handleChange}
                      required
                      className="modern-input"
                    />
                  </div>
                  <div className="input-group">
                    <label>New Password</label>
                    <input
                      type="password"
                      name="newPassword"
                      placeholder="Enter new password"
                      value={passwordData.newPassword}
                      onChange={handleChange}
                      required
                      className="modern-input"
                    />
                  </div>
                  <button type="submit" className="primary-glow-btn">
                    Update Password
                  </button>
                  {message && (
                    <div className={`message-box ${message.includes('✅') ? 'success' : 'error'}`}>
                      {message}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Plan & Support */}
          <div className="grid-right">
            <div className="glass-card plan-highlight-card">
               <div className="card-glow-border"></div>
               <div className="card-header flex-header">
                 <h2>Current Plan</h2>
                 <span className={`plan-badge badge-${planDetails.planName.toLowerCase()}`}>{planDetails.planName}</span>
               </div>
               <div className="card-content">
                 <ul className="plan-feature-list">
                   <li>
                     <span className="check-icon">✓</span>
                     <span>Track Top Gainers & Losers</span>
                   </li>
                   <li>
                     <span className="check-icon">✓</span>
                     <span>Comprehensive Company Insights</span>
                   </li>
                   <li>
                     <span className="check-icon">✓</span>
                     <span>Global Market Coverage</span>
                   </li>
                   {(planDetails.planName === 'Gold' || planDetails.planName === 'Diamond') && (
                     <li>
                        <span className="check-icon">✓</span>
                        <span>Advanced Analytics</span>
                     </li>
                   )}
                   {planDetails.planName === 'Diamond' && (
                     <li>
                        <span className="check-icon">✓</span>
                        <span>Priority Support & VIP Features</span>
                     </li>
                   )}
                 </ul>
                 <button className="upgrade-premium-btn">
                   <span className="btn-text">Upgrade Plan</span>
                 </button>
               </div>
            </div>

            <div className="glass-card support-card">
              <div className="card-header">
                <h2>Customer Support</h2>
              </div>
              <div className="card-content">
                <div className="support-module">
                  <h4 className="module-title">Active Queries</h4>
                  <div className="ticket-list">
                    {supportQueries.map((query) => (
                      <div key={query.id} className="ticket-item">
                        <div className="ticket-top">
                          <span className="ticket-id">{query.id}</span>
                          <span className="ticket-status">{query.status}</span>
                        </div>
                        <p className="ticket-type">{query.type}</p>
                        <p className="ticket-date">{query.date}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="support-module">
                  <h4 className="module-title">Contact Us 24x7</h4>
                  <div className="contact-methods">
                    <button type="button" className="contact-btn" onClick={() => alert('Chat support coming soon!')}>
                      <div className="contact-icon">{supportContacts.chat.icon}</div>
                      <div className="contact-info">
                        <span className="contact-label">{supportContacts.chat.label}</span>
                        <span className="contact-desc">{supportContacts.chat.description}</span>
                      </div>
                    </button>
                    
                    <a href={`tel:${supportContacts.call.value?.replace(/\s/g, '')}`} className="contact-btn">
                      <div className="contact-icon">{supportContacts.call.icon}</div>
                      <div className="contact-info">
                        <span className="contact-label">{supportContacts.call.label}</span>
                        <span className="contact-desc">{supportContacts.call.value}</span>
                      </div>
                    </a>
                    
                    <a href={`mailto:${supportContacts.email.value}`} className="contact-btn">
                      <div className="contact-icon">{supportContacts.email.icon}</div>
                      <div className="contact-info">
                        <span className="contact-label">{supportContacts.email.label}</span>
                        <span className="contact-desc">{supportContacts.email.value}</span>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
